/**
 * The drill-down: the offending rows behind one KPI.
 *
 * This component is the reason the whole feature is worth building. Every graph defect ever found in
 * this programme (the orphan instance, the 19-documents-out-of-20, the dead index) was found by
 * reading a LIST, never by reading a number. The scalar on the tile is only the cardinal of this set.
 */
import { useState } from 'react';
import {
  DataTable,
  InlineLoading,
  InlineNotification,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import { useQuery } from '@tanstack/react-query';
import { explorationApi } from '@/lib/api/exploration.api';
import type { GraphKpiId, GraphKpiRowsPage } from '@snxt/snxt-onto-share';
import { formatRowColumn } from './kpiDisplay';

const PAGE_SIZES = [25, 50, 100];

interface KpiRowsTableProps {
  explorationId: string;
  kpiId: GraphKpiId;
  /** From the summary, so the pager has a total before the first page lands. */
  expectedTotal: number;
}

export function KpiRowsTable({ explorationId, kpiId, expectedTotal }: KpiRowsTableProps) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const offset = (page - 1) * pageSize;

  const { data, isLoading, isError, error } = useQuery<GraphKpiRowsPage>({
    queryKey: ['graph-kpi-rows', explorationId, kpiId, pageSize, offset],
    queryFn: () => explorationApi.getKpiRows(explorationId, kpiId, pageSize, offset),
  });

  if (isLoading) {
    return (
      <div className="kpi-rows__state" data-testid="kpi-rows-loading">
        <InlineLoading description="Loading rows…" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="kpi-rows__state">
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          title="Failed to load rows"
          subtitle={(error as Error)?.message}
        />
      </div>
    );
  }

  const rows = data?.rows ?? [];
  const total = data?.total ?? expectedTotal;

  if (rows.length === 0) {
    return (
      <div className="kpi-rows__state" data-testid="kpi-rows-empty">
        No rows on this page.
      </div>
    );
  }

  // Columns are derived from the rows themselves: the backend returns a generic `detail` bag per
  // KPI, so hardcoding a column list here would mean editing this file for every new KPI.
  const detailKeys = Array.from(
    rows.reduce((keys, row) => {
      Object.keys(row.detail ?? {}).forEach((k) => keys.add(k));
      return keys;
    }, new Set<string>())
  );

  const headers = [
    { key: 'label', header: 'Item' },
    ...detailKeys.map((k) => ({ key: k, header: formatRowColumn(k) })),
  ];

  const tableRows = rows.map((row) => {
    // `id` is declared, not merely present in the bag: Carbon's DataTable requires it and a
    // `Record<string, string>` does not prove it.
    const cells: { id: string; label: string } & Record<string, string> = {
      id: row.id,
      label: row.label,
    };
    for (const key of detailKeys) {
      const value = row.detail?.[key];
      cells[key] = value === null || value === undefined ? '—' : String(value);
    }
    return cells;
  });

  return (
    <div className="kpi-rows" data-testid={`kpi-rows-${kpiId}`}>
      <DataTable rows={tableRows} headers={headers}>
        {({ rows: dtRows, headers: dtHeaders, getTableProps, getHeaderProps, getRowProps }) => (
          <Table {...getTableProps()} size="sm">
            <TableHead>
              <TableRow>
                {dtHeaders.map((header) => (
                  <TableHeader {...getHeaderProps({ header })} key={header.key}>
                    {header.header}
                  </TableHeader>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {dtRows.map((row) => (
                <TableRow {...getRowProps({ row })} key={row.id}>
                  {row.cells.map((cell) => (
                    <TableCell key={cell.id}>{String(cell.value)}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DataTable>

      <Pagination
        page={page}
        pageSize={pageSize}
        pageSizes={PAGE_SIZES}
        totalItems={total}
        size="sm"
        onChange={({ page: nextPage, pageSize: nextSize }) => {
          setPage(nextPage);
          setPageSize(nextSize);
        }}
      />
    </div>
  );
}

// Made with Bob
